import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';

type LanguageCode = 'en' | 'es' | 'fr' | 'de' | 'it';

interface LanguageSwitcherProps {
  variant?: 'default' | 'auth';
}

interface LanguageOption {
  code: LanguageCode;
  name: string;
}

const FlagIcon: React.FC<{ code: LanguageCode; name: string }> = ({ code, name }) => {
  const frameClassName = 'h-5 w-7 overflow-hidden rounded-[4px] ring-1 ring-white/20 shadow-sm';

  switch (code) {
    case 'en':
      return (
        <span className={frameClassName} aria-hidden="true">
          <svg viewBox="0 0 28 20" className="h-full w-full" role="img" aria-label={name}>
            <rect width="28" height="20" fill="#1F4AA8" />
            <path d="M0 0l28 20M28 0L0 20" stroke="#FFFFFF" strokeWidth="5" />
            <path d="M0 0l28 20M28 0L0 20" stroke="#D81E34" strokeWidth="2.2" />
            <path d="M14 0v20M0 10h28" stroke="#FFFFFF" strokeWidth="7" />
            <path d="M14 0v20M0 10h28" stroke="#D81E34" strokeWidth="4" />
          </svg>
        </span>
      );
    case 'es':
      return (
        <span className={frameClassName} aria-hidden="true">
          <svg viewBox="0 0 28 20" className="h-full w-full" role="img" aria-label={name}>
            <rect width="28" height="20" fill="#C81D25" />
            <rect y="5" width="28" height="10" fill="#F4C430" />
          </svg>
        </span>
      );
    case 'fr':
      return (
        <span className={frameClassName} aria-hidden="true">
          <svg viewBox="0 0 28 20" className="h-full w-full" role="img" aria-label={name}>
            <rect width="9.34" height="20" fill="#1F4AA8" />
            <rect x="9.33" width="9.34" height="20" fill="#FFFFFF" />
            <rect x="18.66" width="9.34" height="20" fill="#D81E34" />
          </svg>
        </span>
      );
    case 'de':
      return (
        <span className={frameClassName} aria-hidden="true">
          <svg viewBox="0 0 28 20" className="h-full w-full" role="img" aria-label={name}>
            <rect width="28" height="6.67" fill="#111827" />
            <rect y="6.67" width="28" height="6.67" fill="#C81D25" />
            <rect y="13.34" width="28" height="6.66" fill="#F4C430" />
          </svg>
        </span>
      );
    case 'it':
    default:
      return (
        <span className={frameClassName} aria-hidden="true">
          <svg viewBox="0 0 28 20" className="h-full w-full" role="img" aria-label={name}>
            <rect width="9.34" height="20" fill="#169B62" />
            <rect x="9.33" width="9.34" height="20" fill="#FFFFFF" />
            <rect x="18.66" width="9.34" height="20" fill="#D81E34" />
          </svg>
        </span>
      );
  }
};

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ variant = 'default' }) => {
  const { i18n, t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const activeLanguageCode = ((i18n.resolvedLanguage || i18n.language || 'en').split('-')[0] as LanguageCode);

  const languages: LanguageOption[] = [
    { code: 'en', name: t('languages.english') },
    { code: 'es', name: t('languages.spanish') },
    { code: 'fr', name: t('languages.french') },
    { code: 'de', name: t('languages.german') },
    { code: 'it', name: t('languages.italian') },
  ];

  const currentLanguage = languages.find((lang) => lang.code === activeLanguageCode) || languages[0];
  const isAuthVariant = variant === 'auth';

  const buttonClassName = isAuthVariant
    ? 'flex items-center gap-2 rounded-xl border border-violet-200/15 bg-gradient-to-r from-violet-900/80 via-purple-900/75 to-fuchsia-900/70 px-3 py-2 text-white shadow-lg shadow-slate-950/30 transition-all duration-300 hover:from-violet-800/85 hover:via-purple-800/80 hover:to-fuchsia-800/75 hover:shadow-xl hover:shadow-slate-950/40'
    : 'flex items-center gap-2 rounded-lg border border-slate-600/30 bg-slate-800/50 px-3 py-2 text-white transition-all duration-300 hover:border-slate-500/50 hover:bg-slate-700/50';

  const dropdownClassName = isAuthVariant
    ? 'absolute top-full right-0 z-50 mt-2 min-w-52 overflow-hidden rounded-xl bg-gradient-to-br from-slate-950/96 via-violet-950/88 to-slate-950/96 shadow-2xl shadow-slate-950/50 backdrop-blur-xl'
    : 'absolute top-full right-0 z-50 mt-2 min-w-48 overflow-hidden rounded-xl border border-slate-700 bg-slate-800 shadow-xl';

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLanguageChange = (languageCode: LanguageCode) => {
    i18n.changeLanguage(languageCode);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button onClick={() => setIsOpen(!isOpen)} className={buttonClassName}>
        <FlagIcon code={currentLanguage.code} name={currentLanguage.name} />
        <span className="hidden text-sm font-medium sm:inline">{currentLanguage.name}</span>
        <ChevronDown size={14} className={isAuthVariant ? 'text-white/80' : 'text-slate-400'} />
      </button>

      {isOpen && (
        <div className={dropdownClassName}>
          <div className="py-2">
            {languages.map((language) => (
              <button
                key={language.code}
                onClick={() => handleLanguageChange(language.code)}
                className={`flex w-full items-center justify-between px-4 py-3 text-left transition-colors ${
                  activeLanguageCode === language.code
                    ? isAuthVariant
                      ? 'bg-white/10 text-violet-50'
                      : 'bg-slate-700/50 text-blue-400'
                    : isAuthVariant
                      ? 'text-white hover:bg-white/6'
                      : 'text-white hover:bg-slate-700'
                }`}
              >
                <div className="flex items-center gap-3">
                  <FlagIcon code={language.code} name={language.name} />
                  <span className="font-medium">{language.name}</span>
                </div>
                {activeLanguageCode === language.code && (
                  <Check size={16} className={isAuthVariant ? 'text-violet-100' : 'text-blue-400'} />
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
